import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { stageEntrants, drawWinners, entrantList, winnerList, nonWinnerList } from "../../api/lotteryApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");

const emptyEntrant = () => ({
  key: Math.random(),
  customStudentId: "",
  studentName: "",
  studentGender: "",
  studentReligion: "",
  guardianMobile: "",
  fatherName: "",
  motherName: "",
  bloodGroup: "",
});

/**
 * Super-admin only — digicampusbd's {@code LotteryWinStudentController} had
 * no sidebar link anywhere (not even a hardcoded one, unlike onlineapplication),
 * reachable only by navigating to its URL directly. See MIGRATION.md Phase
 * 10 notes. One page folds the old app's 6 separate lottery screens (stage,
 * draw, class list, win list, non-win list, SMS-send-to-non-winners — the
 * SMS-send screens aren't ported, same as every other page's own SMS
 * shortcuts since Phase 9's SmsPage already covers manual sending) into
 * Stage / Draw / Results tabs.
 */
export default function LotteryPage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [tab, setTab] = useState("stage");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    classApi.list().then(setClasses);
  }, []);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Lottery</h1>
      </div>
      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">Select a class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <Tabs
        tabs={[
          { key: "stage", label: "Stage Entrants" },
          { key: "draw", label: "Draw" },
          { key: "results", label: "Results" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {!classId && <p>Select a class to continue.</p>}
      {classId && tab === "stage" && <StageTab classId={classId} setError={setError} setSuccess={setSuccess} />}
      {classId && tab === "draw" && <DrawTab classId={classId} setError={setError} setSuccess={setSuccess} />}
      {classId && tab === "results" && <ResultsTab classId={classId} setError={setError} />}
    </MainLayout>
  );
}

function StageTab({ classId, setError, setSuccess }) {
  const [entrants, setEntrants] = useState([emptyEntrant()]);
  const [saving, setSaving] = useState(false);

  const setField = (key, field, value) =>
    setEntrants((prev) => prev.map((e) => (e.key === key ? { ...e, [field]: value } : e)));
  const addRow = () => setEntrants((prev) => [...prev, emptyEntrant()]);
  const removeRow = (key) => setEntrants((prev) => prev.filter((e) => e.key !== key));

  const handleSubmit = async () => {
    setError("");
    const usable = entrants.filter((e) => e.customStudentId.trim() && e.studentName.trim());
    if (usable.length === 0) {
      setError("Add at least one entrant with an ID and name.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        classId: Number(classId),
        entrants: usable.map(({ key, ...rest }) => rest),
      };
      const res = await stageEntrants(payload);
      setSuccess(res.message);
      setEntrants([emptyEntrant()]);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to stage entrants.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="table-scroll">
      <table className="attendance-table">
        <thead>
          <tr>
            <th>Student ID</th>
            <th>Name</th>
            <th>Gender</th>
            <th>Religion</th>
            <th>Guardian Mobile</th>
            <th>Father Name</th>
            <th>Mother Name</th>
            <th>Blood Group</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {entrants.map((e) => (
            <tr key={e.key}>
              <td>
                <input value={e.customStudentId} onChange={(ev) => setField(e.key, "customStudentId", ev.target.value)} />
              </td>
              <td>
                <input value={e.studentName} onChange={(ev) => setField(e.key, "studentName", ev.target.value)} />
              </td>
              <td>
                <select value={e.studentGender} onChange={(ev) => setField(e.key, "studentGender", ev.target.value)}>
                  <option value="">-</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </td>
              <td>
                <input value={e.studentReligion} onChange={(ev) => setField(e.key, "studentReligion", ev.target.value)} />
              </td>
              <td>
                <input value={e.guardianMobile} onChange={(ev) => setField(e.key, "guardianMobile", ev.target.value)} />
              </td>
              <td>
                <input value={e.fatherName} onChange={(ev) => setField(e.key, "fatherName", ev.target.value)} />
              </td>
              <td>
                <input value={e.motherName} onChange={(ev) => setField(e.key, "motherName", ev.target.value)} />
              </td>
              <td>
                <input value={e.bloodGroup} onChange={(ev) => setField(e.key, "bloodGroup", ev.target.value)} />
              </td>
              <td>
                {entrants.length > 1 && (
                  <button type="button" className="btn-link danger" onClick={() => removeRow(e.key)}>
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="form-actions" style={{ justifyContent: "space-between" }}>
        <button type="button" className="btn-link" onClick={addRow}>
          + Add Row
        </button>
        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving..." : "Stage Entrants"}
        </button>
      </div>
    </>
  );
}

function DrawTab({ classId, setError, setSuccess }) {
  const [numberOfSeat, setNumberOfSeat] = useState("");
  const [drawing, setDrawing] = useState(false);

  const handleDraw = async () => {
    setError("");
    if (!numberOfSeat || Number(numberOfSeat) <= 0) {
      setError("Enter a valid number of seats.");
      return;
    }
    if (!window.confirm(`Draw ${numberOfSeat} winner(s) for this class? This can only be done once per class.`)) {
      return;
    }
    setDrawing(true);
    try {
      const res = await drawWinners({ classId: Number(classId), numberOfSeat: Number(numberOfSeat) });
      setSuccess(res.message);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to draw winners.");
    } finally {
      setDrawing(false);
    }
  };

  return (
    <>
      <label>Number of Seats</label>
      <input
        type="number"
        min="1"
        style={{ maxWidth: 200 }}
        value={numberOfSeat}
        onChange={(e) => setNumberOfSeat(e.target.value)}
      />
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleDraw} disabled={drawing}>
          {drawing ? "Drawing..." : "Draw Winners"}
        </button>
      </div>
    </>
  );
}

function ResultsTab({ classId, setError }) {
  const [sub, setSub] = useState("entrants");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const request = sub === "entrants" ? entrantList(classId) : sub === "winners" ? winnerList(classId) : nonWinnerList(classId);
    request
      .then(setRows)
      .catch(() => setError("Failed to load list."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, classId]);

  return (
    <>
      <Tabs
        tabs={[
          { key: "entrants", label: "All Entrants" },
          { key: "winners", label: "Winners" },
          { key: "non-winners", label: "Non-Winners" },
        ]}
        active={sub}
        onChange={setSub}
      />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "customStudentId", label: "Student ID" },
            { key: "studentName", label: "Name" },
            { key: "studentGender", label: "Gender" },
            { key: "fatherName", label: "Father" },
            { key: "motherName", label: "Mother" },
            { key: "guardianMobile", label: "Guardian Mobile" },
          ]}
          rows={rows}
        />
      )}
    </>
  );
}
